import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { Clock, Info } from "lucide-react";
import type { MFDeadlineExtension } from "state/members/accessReviews";
import {
  deadlineExtensionBannerState,
  pendingExtensionBannerText,
} from "../reviewLogic";

/**
 * DeadlineExtensionBanner — surfaces the reviewer's deadline-extension state on
 * the review page (PLO-4856), driven entirely by reviewLogic's
 * `deadlineExtensionBannerState`:
 *   - "pending": a neutral/info chip "Extension requested (N days) — awaiting
 *                approval" (the request action is disabled while this shows).
 *   - "denied":  the most recent request's denial reason, shown unobtrusively
 *                (the reviewer may re-request if they still have allowance).
 *   - "granted"/null: nothing — the granted state simply shows in the due date.
 */
export interface DeadlineExtensionBannerProps {
  deadlineExtension: MFDeadlineExtension | null | undefined;
}

const DeadlineExtensionBanner = ({
  deadlineExtension,
}: DeadlineExtensionBannerProps) => {
  const state = deadlineExtensionBannerState(deadlineExtension);

  if (state === "pending") {
    return (
      <Flex
        align="center"
        gap="8px"
        border="1px solid"
        borderColor="orange.200"
        bg="orange.50"
        borderRadius="8px"
        px={3}
        py={2}
        data-testid="extension-pending-banner"
      >
        <Icon as={Clock} boxSize="16px" color="orange.500" flexShrink={0} />
        <Text fontSize="sm" color="orange.700" fontWeight="500">
          {pendingExtensionBannerText(deadlineExtension)}
        </Text>
      </Flex>
    );
  }

  if (state === "denied") {
    return (
      <HStack
        align="flex-start"
        gap="8px"
        color="gray.500"
        data-testid="extension-denied-note"
      >
        <Icon as={Info} boxSize="14px" color="gray.400" mt="2px" flexShrink={0} />
        <Box>
          <Text fontSize="xs" color="gray.500">
            Your last extension request was declined.
          </Text>
          <Text fontSize="xs" color="gray.500">
            {deadlineExtension?.deniedReason}
          </Text>
        </Box>
      </HStack>
    );
  }

  return null;
};

export default DeadlineExtensionBanner;
